// ============================================
// Audio System — replaces AudioManager.cs
// 3 channels: BGM (crossfade), Ambient (crossfade), SFX (pooled)
// Powered by Howler.js (Web Audio API with HTML5 fallback)
// ============================================

import { events, Events } from '../engine/events.js';

const CROSSFADE_DURATION = 1000; // ms total (500 fade out + 500 fade in)

// All tracks enabled — previously disabled bar/outside/mall/salon re-enabled (better than silence)
const DISABLED_BGM = new Set(['bar', 'salon']);
const DISABLED_AMBIENT = new Set(['amb_bar', 'amb_salon']);

/* global Howl, Howler */

class AudioManager {
    constructor() {
        // BGM state
        this._bgm = null;        // current Howl instance
        this._bgmTrack = '';

        // Ambient state
        this._ambient = null;
        this._ambientTrack = '';

        // Hypno voice state (v0.2.9). The audio-bearing hypno clips carry their
        // voice in the <video>'s OWN audio track (no separate file). Android
        // WebView can't reliably mute/stop a native video-audio session once it
        // starts (Chromium bug 532359: video.muted AFTER play is a no-op), so we
        // route the video's audio through a Web Audio GainNode
        // (createMediaElementSource → gain → destination) on a dedicated
        // AudioContext. The gain is reliably controllable on every platform (same
        // primitive as BGM/ambient), so 🔊 and stop work on Android. The element
        // stays muted=true for silent autoplay until the first 🔊-ON gesture sets
        // muted=false; re-mute is gain=0, NEVER element.muted=true again (the bug).
        this._hypnoCtx = null;        // dedicated AudioContext (lazy)
        this._hypnoSource = null;     // MediaElementAudioSourceNode for the active video
        this._hypnoGain = null;       // GainNode controlling its volume/mute
        this._hypnoVideoEl = null;    // the <video> currently routed
        // Default SOUND ON (owner choice v0.2.9): the hypno voice plays by
        // default; the 🔊/🔇 button is a MUTE toggle (starts 🔊). Carries across
        // beats. Audio is gesture-backed (the choice click that entered the beat).
        this._hypnoMuted = false;     // global 🔊 state (false = sound on)
        // v0.2.8.7 FF152 fix: the video-gain (createMediaElementSource) path is
        // UN-STOPPABLE in Firefox 152 — once a <video> is captured by a
        // MediaElementAudioSourceNode, no element API (pause/muted/volume/src=''/
        // load) and not even ctx.close() stops it; it pops to native output and
        // loops forever (foundby:owner). So the voice now plays as a SEPARATE
        // Howler sound from the extracted .m4a (assets/audio/hypno/<name>.m4a) and
        // the <video> stays muted=true ALWAYS (never sourced). Howl.stop() is
        // reliably stoppable on every platform, incl. Android.
        this._hypnoHowl = null;       // current hypno-voice Howl (m4a)
        this._hypnoHowlName = '';

        // SFX cache (reuse Howl instances for repeated sounds)
        this._sfxCache = new Map();
        this._sfxFailed = new Set();

        // Howls handed to _retire() stay here until their unload actually
        // ran. Lets the volume setters pin mid-crossfade howls to 0 so a
        // stuck one can never re-assert itself audibly.
        this._retired = new Set();

        // Audio manifest — { bgm: {track: [exts]}, ambient: {...}, sfx: {...} }
        // Loaded async at boot. Until loaded, _audioUrl() falls back to the
        // legacy [ogg, mp3, wav] array. After load, returns only existing
        // extensions — fixes Android stock WebView (Infinix) treating
        // SPA-served 404 HTML as malformed media (error=4) and never
        // falling through to the next URL in Howler's array.
        this._audioManifest = null;
        fetch('assets/data/audio_manifest.json', { cache: 'force-cache' })
            .then(r => r.ok ? r.json() : null)
            .then(m => { if (m) this._audioManifest = m; })
            .catch(() => { /* fall back to legacy array */ });

        // Volume settings from localStorage
        this._bgmVolume = this._loadFloat('BGMVolume', 0.56);
        this._sfxVolume = this._loadFloat('SFXVolume', 0.8);
        // Dedicated hypno-voice channel (own Settings slider), independent of SFX.
        this._hypnoVolume = this._loadFloat('HypnoVolume', 0.8);
        // html5 audio pool headroom (VPS report #393, 0.2.9.0.6 Firefox
        // marathon streamer: "HTML5 Audio pool exhausted" after days of
        // stream/sleep cycles). Howler seeds html5PoolSize unlocked <audio>
        // nodes on the first gesture; a node obtained while the pool is
        // EMPTY is created fresh, counts as potentially locked under
        // Firefox autoplay policy, and never returns to the pool — so every
        // churn spike past the pool depth (bgm + ambient + retiring
        // crossfades) permanently shrinks it. 10 → 20 gives the spike
        // headroom; cost is ~10 idle <audio> elements, a few KB.
        try { if (typeof Howler !== 'undefined') Howler.html5PoolSize = 20; } catch (e) { /* Howler absent in tests */ }
        // Restore global mute state at boot — players who saved 0/0 in a
        // prior session need silence before the first slider interaction.
        // Howler's ctx isn't ready yet at constructor time, so defer to
        // the first user-interaction unlock.
        setTimeout(() => this._applyGlobalMute(), 0);

        // Ensure AudioContext is unlocked on first user interaction
        this._unlocked = false;
        const unlock = () => {
            if (this._unlocked) return;
            this._unlocked = true;
            if (typeof Howler !== 'undefined' && Howler.ctx && Howler.ctx.state === 'suspended') {
                Howler.ctx.resume().then(() => console.log('[Audio] AudioContext resumed'));
            }
            document.removeEventListener('click', unlock);
            document.removeEventListener('touchstart', unlock);
            document.removeEventListener('keydown', unlock);
        };
        document.addEventListener('click', unlock);
        document.addEventListener('touchstart', unlock);
        document.addEventListener('keydown', unlock);

        // Listen for tag events
        events.on(Events.TAG_BGM, (trackName) => this.playBGM(trackName));
        events.on(Events.TAG_SFX, (sfxName) => this.playSFX(sfxName));
        events.on(Events.AUDIO_SFX_PLAY, (sfxName) => this.playSFX(sfxName));
        events.on(Events.TAG_AMBIENT, (trackName) => {
            if (trackName === 'stop') this.stopAmbient();
            else this.playAmbient(trackName);
        });

        // RACE-FREE hypno-audio stop (v0.2.9). Fired SYNCHRONOUSLY at the very top
        // of every refreshView(), BEFORE any clear/rebuild — so the LEAVING beat's
        // gain-routed audio is always detached on every advance, regardless of the
        // _videoAddedThisRefresh / scene-change ordering races that let the prior
        // ~10 surface-by-surface fixes leak. The new beat (if any) re-attaches in
        // _addVideo, which runs later in the async _processStoryContent.
        events.on(Events.STORY_STOP_MEDIA, () => this.stopHypnoVoice());

        // FOREGROUND/BACKGROUND leak fix (v0.2.9, owner report "audio plays in the
        // background too"). The gain-routed hypno audio would otherwise keep
        // playing behind other apps. Suspend the dedicated hypno AudioContext when
        // the app/tab hides (reliable cross-platform pause), resume on return if a
        // clip is still routed.
        try {
            document.addEventListener('visibilitychange', () => {
                if (!this._hypnoCtx) return;
                if (document.hidden) { try { this._hypnoCtx.suspend(); } catch (e) {} }
                else if (this._hypnoVideoEl) { try { this._hypnoCtx.resume(); } catch (e) {} }
            });
        } catch (e) { /* no document (headless) */ }
    }

    // ---- Volume control ----

    get bgmVolume() { return this._bgmVolume; }
    set bgmVolume(val) {
        this._bgmVolume = Math.max(0, Math.min(1, val));
        try { localStorage.setItem('BGMVolume', String(this._bgmVolume)); } catch(e) { /* iframe storage blocked */ }
        // iOS Safari HTML5 audio reports .playing()=false during buffering /
        // when the HTML5 audio pool is exhausted, which dropped the volume
        // update silently. Set unconditionally — Howler's volume() is safe
        // on non-playing/loading instances and also cancels any in-flight
        // fade() so the new target sticks.
        if (this._bgm) {
            this._bgm.volume(this._bgmVolume);
        }
        // Retired-but-not-yet-unloaded howls (mid-crossfade) must never
        // re-assert their old volume — pin to 0.
        this._retired.forEach((h) => { try { h.volume(0); } catch (e) { /* unloading */ } });
        this._applyGlobalMute();
    }

    get sfxVolume() { return this._sfxVolume; }
    set sfxVolume(val) {
        this._sfxVolume = Math.max(0, Math.min(1, val));
        try { localStorage.setItem('SFXVolume', String(this._sfxVolume)); } catch(e) { /* iframe storage blocked */ }
        // SFX volume also controls ambient (mirrors Unity)
        if (this._ambient) {
            this._ambient.volume(this._sfxVolume);
        }
        // Apply to any cached SFX Howls that are currently playing (long
        // samples — short clicks naturally pick up new volume on next play).
        this._sfxCache.forEach((howl) => {
            try { howl.volume(this._sfxVolume); } catch (e) {}
        });
        // Same pin as bgmVolume — retired ambient howls share the set.
        this._retired.forEach((h) => { try { h.volume(0); } catch (e) { /* unloading */ } });
        // Native <video> elements (inline story videos, hypno clips, modal
        // expand, gallery viewer) are outside Howler and only snapshot the
        // slider at creation/unmute — sweep them so dragging the slider
        // applies live (slider-to-0 must silence an unmuted hypno voice
        // track). Muted videos keep their muted flag; volume is harmless.
        try {
            document.querySelectorAll('video').forEach((v) => { v.volume = this._sfxVolume; });
        } catch (e) { /* non-DOM context */ }
        this._applyGlobalMute();
    }

    /**
     * Deterministic howl retirement (crossfade-when-safe).
     *
     * The old pattern — `old.fade(v, 0, d); old.once('fade', () => old.unload())`
     * — leaked whenever Howler never delivered that 'fade' to the listener:
     * a no-op fade from volume 0 emits 'fade' SYNCHRONOUSLY inside fade()
     * (before once() on the next line is attached), a fade on a still-loading
     * / play-locked html5 howl only gets QUEUED (and a queued action can be
     * consumed by an unrelated 'fade' without running), so unload never ran.
     * The orphaned loop:true howl then kept playing forever — and re-faded
     * itself back to full volume on every html5 loop restart via its captured
     * onplay closure — unreachable by the per-channel volume sliders ("sound
     * keeps spamming, several copies out of sync, sliders don't help";
     * empirically reproduced 2026-06-07 at up to 92% rate for track switches
     * within ~60ms of the previous one, e.g. the sleep → dream → morning
     * #bgm chain). Teardown must never depend on the 'fade' event alone.
     */
    _retire(howl, fadeMs) {
        if (!howl) return;
        this._retired.add(howl);

        let done = false;
        const kill = () => {
            if (done) return;
            done = true;
            this._retired.delete(howl);
            try { howl.unload(); } catch (e) { /* already unloaded */ }
        };

        let vol = 0;
        let loaded = false;
        try { vol = howl.volume(); } catch (e) { /* treat as silent */ }
        try { loaded = howl.state() === 'loaded'; } catch (e) { /* treat as not loaded */ }

        // Fade is pointless or unreliable here — tear down immediately.
        // (vol 0 → no-op fade emits 'fade' synchronously / never;
        //  not loaded → fade() only queues and may never run;
        //  fadeMs 0 → caller wants a hard stop.)
        if (!loaded || !(vol > 0) || !(fadeMs > 0)) {
            kill();
            return;
        }

        try {
            howl.once('fade', kill);     // attach BEFORE fade() — no-op fades emit synchronously
            howl.fade(vol, 0, fadeMs);
        } catch (e) {
            kill();
            return;
        }
        // Backstop: unload even if 'fade' was consumed by another fade,
        // re-queued behind a play-lock, or never fires at all.
        setTimeout(kill, fadeMs + 250);
    }

    /** Global Howler mute when BOTH sliders are at 0. Catches any Howl
     *  instance we don't track per-channel — splash voice, future modules,
     *  third-party plugins. Player report @Brendo 2026-05-14: sliders to 0
     *  didn't actually silence the tab. */
    _applyGlobalMute() {
        if (typeof Howler === 'undefined') return;
        try { Howler.mute(this._bgmVolume === 0 && this._sfxVolume === 0); }
        catch (e) { /* Howler.mute can throw if ctx not yet initialised */ }
    }

    // ---- BGM ----

    playBGM(trackName) {
        if (trackName === this._bgmTrack) return;
        if (this._bgmChanging) return; // prevent re-entrance during fade
        this._bgmChanging = true;

        if (!trackName || trackName === 'none' || trackName === 'stop') {
            this.stopBGM();
            this._bgmChanging = false;
            return;
        }

        if (DISABLED_BGM.has(trackName)) {
            this.stopBGM();
            this._bgmChanging = false;
            return;
        }

        this._bgmTrack = trackName;
        const url = this._bgmUrl(trackName);
        const fadeDuration = CROSSFADE_DURATION / 2;

        console.log(`[Audio] Playing BGM: ${trackName} → ${JSON.stringify(url)} vol=${this._bgmVolume}`);
        let fadedIn = false;
        const newHowl = new Howl({
            src: url,
            loop: true,
            volume: 0,
            html5: true, // streaming for long tracks — no decode delay
            onloaderror: (id, err) => {
                console.warn(`[Audio] BGM format unavailable: ${trackName} (error=${err})`);
                // Reset state, or the manager stays stuck claiming it plays the
                // failed track forever: the same-track early-return above would
                // no-op every retry of this track name (missing-file #bgm: tags
                // — warm/reflective/resort_lobby/... — hit this path).
                if (this._bgm === newHowl) {
                    this._bgm = null;
                    this._bgmTrack = '';
                }
                try { newHowl.unload(); } catch (e) { /* never loaded */ }
                this._bgmChanging = false;
            },
            onload: () => {
                console.log(`[Audio] BGM loaded: ${trackName} (${Math.round(newHowl.duration())}s)`);
            },
            onplay: () => {
                // Fade in ONCE. html5 looping re-emits 'play' on every loop
                // restart (stop+play internally) — re-fading from 0 each time
                // produced an audible dip at every loop point, and on a
                // stuck-retired howl the same closure was what faded an
                // orphan back up to full volume.
                if (fadedIn) return;
                fadedIn = true;
                newHowl.fade(0, this._bgmVolume, fadeDuration);
            },
        });

        // Fade out old, then start new — deterministic teardown (see _retire)
        if (this._bgm) {
            this._retire(this._bgm, fadeDuration);
        }

        this._bgm = newHowl;
        newHowl.play();
        this._bgmChanging = false;
    }

    stopBGM() {
        this._bgmTrack = '';
        if (this._bgm) {
            this._retire(this._bgm, CROSSFADE_DURATION / 2);
            this._bgm = null;
        }
    }

    // ---- Ambient ----

    playAmbient(trackName) {
        if (!trackName || trackName === this._ambientTrack) return;

        if (DISABLED_AMBIENT.has(trackName)) {
            this.stopAmbient();
            return;
        }

        this._ambientTrack = trackName;
        const url = this._ambientUrl(trackName);

        // Stop old ambient (hard stop, no fade — fadeMs 0 = immediate kill,
        // but with the _retire bookkeeping so a play-locked html5 node can't
        // slip back out of the pool still playing)
        if (this._ambient) {
            this._retire(this._ambient, 0);
            this._ambient = null;
        }

        this._ambient = new Howl({
            src: url,
            loop: true,
            volume: this._sfxVolume,
            html5: true,
            onloaderror: () => {
                console.warn(`[Audio] Ambient not found: ${url}`);
            },
        });

        this._ambient.play();
    }

    stopAmbient() {
        this._ambientTrack = '';
        if (this._ambient) {
            this._retire(this._ambient, 500);
            this._ambient = null;
        }
    }

    // ---- Hypno voice (Web Audio, reliably stop/mute-able on Android) ----

    /**
     * Play the voice track for an audio-bearing hypno clip. html5:false forces
     * Web Audio (the GainNode path), so volume(0)/stop() actually silence it on
     * Android WebView — unlike the <video> element's native audio player. Starts
     * at the current mute state (muted by default, like the old muted-autoplay).
     */
    /** Lazily create + resume the dedicated hypno AudioContext. Separate from
     *  Howler's ctx so Howler's 30s autoSuspend can't silence a held hypno video,
     *  and so the hypno slider stays fully independent. */
    _ensureHypnoCtx() {
        if (!this._hypnoCtx) {
            try {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                this._hypnoCtx = Ctx ? new Ctx() : null;
            } catch (e) { this._hypnoCtx = null; }
        }
        if (this._hypnoCtx && this._hypnoCtx.state === 'suspended') {
            this._hypnoCtx.resume().catch(() => {});
        }
        return this._hypnoCtx;
    }

    /** Route a hypno <video>'s OWN audio through a controllable GainNode. The
     *  element keeps muted=true (silent autoplay) until the first 🔊-ON gesture.
     *  Only ONE hypno video is routed at a time — attaching detaches any prior. */
    attachHypnoVideo(videoEl) {
        if (!videoEl) return;
        if (this._hypnoVideoEl === videoEl) return; // already routed
        this.detachHypnoVideo();
        const ctx = this._ensureHypnoCtx();
        if (!ctx) return; // no Web Audio — element stays muted=true (silent), no voice
        try {
            this._hypnoSource = ctx.createMediaElementSource(videoEl);
            this._hypnoGain = ctx.createGain();
            // gain=0 guarantees silence even if the engine feeds the node a
            // non-muted signal; reflects the global 🔊 state.
            this._hypnoGain.gain.value = this._hypnoMuted ? 0 : this._hypnoVolume;
            this._hypnoSource.connect(this._hypnoGain);
            this._hypnoGain.connect(ctx.destination);
            this._hypnoVideoEl = videoEl;
            // If unmuted carried across beats, open the element so the node gets signal.
            if (!this._hypnoMuted) { try { videoEl.muted = false; } catch (e) {} }
        } catch (e) {
            // createMediaElementSource throws if the element was already routed
            // (shouldn't happen — fresh element per beat) or ctx is unusable.
            this._hypnoSource = null; this._hypnoGain = null; this._hypnoVideoEl = null;
        }
    }

    /** Disconnect the routed hypno video's audio graph. Idempotent. Forces the
     *  element back to muted=true so it can never emit native audio post-detach. */
    detachHypnoVideo() {
        // ORDER IS LOAD-BEARING: kill the ELEMENT *before* disconnecting the
        // graph. The element is muted=false by design (so the GainNode receives
        // signal — the 🔊 button only zeroes the gain, never element.muted). If
        // we disconnect the MediaElementSource while the element still has live,
        // un-muted audio, FIREFOX re-routes that audio to the element's NATIVE
        // output: the voice "restarts" (audibly, loud) and loops, and because the
        // element is then detached from the DOM, pause()/load()/removeAttribute
        // can no longer stop it (foundby:owner 0.2.8.6 FF152 — "mute the video,
        // hit Continue, the sound starts over and loops; __killHypnoAudio doesn't
        // stop it"). So: mute + pause + fully UNLOAD the element FIRST (while the
        // source is still connected, so there is no audio left to re-route), THEN
        // tear down the graph.
        try {
            if (this._hypnoVideoEl) {
                const el = this._hypnoVideoEl;
                el.loop = false;
                el.pause();
                // Empty the element FIRST...
                try { el.removeAttribute('src'); el.src = ''; el.load(); } catch (e2) {}
                // ...THEN mute + zero volume. load() RESETS muted->false and
                // volume->1 (element defaults), so setting them before load() is
                // wiped — that was the self-inflicted bug: the element ended up
                // un-muted, and when ctx.close() releases it to Firefox's native
                // output it played LOUD and looped. Setting them AFTER load()
                // (and volume=0 as belt — muted is a no-op while a source exists)
                // keeps it silent through the close.
                try { el.muted = true; el.volume = 0; el.pause(); } catch (e3) {}
            }
        } catch (e) {}
        try { if (this._hypnoSource) this._hypnoSource.disconnect(); } catch (e) {}
        try { if (this._hypnoGain) this._hypnoGain.disconnect(); } catch (e) {}
        this._hypnoSource = null; this._hypnoGain = null; this._hypnoVideoEl = null;
        // CLOSE (not suspend) the dedicated ctx, then null it so _ensureHypnoCtx
        // builds a fresh one on the next attach. suspend() does NOT stop a
        // MediaElementAudioSourceNode in Firefox 152: the captured <video> pops
        // to native output and loops forever, and a re-entry stacks a SECOND
        // createMediaElementSource on the same still-alive ctx (overlap). close()
        // destroys every node on the context — including the source node that
        // captured the element — ending the capture definitively. The audio call
        // log (FF, 2026-06-22) confirmed detach only ever suspended; each hypno
        // re-entry created a new createMES on the same ctx without the old one
        // being torn down. Recreating the ctx per session is cheap (hypno is
        // infrequent).
        try { if (this._hypnoCtx && this._hypnoCtx.state !== 'closed') this._hypnoCtx.close(); } catch (e) {}
        this._hypnoCtx = null;
    }

    /** Play a hypno clip's voice via RAW Web Audio (decoded .ogg -> BufferSource
     *  -> GainNode -> destination), as a ONE-SHOT (loop=false).
     *
     *  Why one-shot and NOT loop=true: Firefox 152 cannot stop a NATIVE
     *  loop=true AudioBufferSourceNode. Live instrument proof (foundby:owner,
     *  hypno_spiral_obey): src.stop() fires, the 'ended' event fires, AND
     *  ctx.close() runs (state went to closed) — yet the audio keeps looping.
     *  This is the Web-Audio analogue of the FF152 media-element "won't stop"
     *  bug, and it is SPECIFIC to native looping sources: in the same log every
     *  loop=false source (SFX, BGM segments) ended cleanly. Howler loops BGM via
     *  onended->replay with loop=false segments, which is exactly why BGM stops
     *  fine on FF152 and a native loop did not.
     *
     *  A one-shot source therefore (a) stops on src.stop() at Continue, and
     *  (b) even if a future FF regression no-ops mid-play stop, it ENDS by
     *  itself after the clip length — so an infinite loop is impossible. The
     *  <video> is muted=true and (for voice clips) loop=false, so it freezes on
     *  its last frame; the Replay button restarts video+voice once. */
    playHypnoVoice(name) {
        if (!name) return;
        this.stopHypnoVoice();   // never overlap a prior voice on re-entry
        const ctx = this._ensureHypnoCtx();
        if (!ctx) return;
        const gen = (this._hypnoGen = (this._hypnoGen || 0) + 1);
        if (!this._hypnoBuffers) this._hypnoBuffers = new Map();
        const startBuf = (buf) => {
            if (gen !== this._hypnoGen || !buf) return;  // superseded by a stop / newer play during the async decode
            try {
                const src = ctx.createBufferSource();
                src.buffer = buf;
                src.loop = false;    // ONE-SHOT — FF152 cannot stop a native loop=true source (see doc above)
                const gain = ctx.createGain();
                gain.gain.value = this._hypnoMuted ? 0 : this._hypnoVolume;
                src.connect(gain); gain.connect(ctx.destination);
                // self-clean when the one-shot finishes so a stale ref can't linger
                src.onended = () => { if (this._hypnoSource === src) { this._hypnoSource = null; this._hypnoGain = null; } try { gain.disconnect(); } catch (e2) {} };
                src.start(0);
                this._hypnoSource = src;
                this._hypnoGain = gain;
                this._hypnoHowlName = name;
                this._duckBgmForHypno(true);   // hypno clip carries its own ambient — silence game BGM
            } catch (e) { /* ctx unusable */ }
        };
        const cached = this._hypnoBuffers.get(name);
        if (cached) { startBuf(cached); return; }
        // Format fallback: Opus/.ogg first (small, Chrome/Firefox) then AAC/.m4a
        // (universal — Safari/iOS decodeAudioData REJECTS Opus-in-ogg, foundby:
        // webkit regression spec 2026-06-22). Without the .m4a fallback the hypno
        // voice was silent on every Safari/iOS client.
        const formats = ['ogg', 'm4a'];
        const tryLoad = (i) => {
            if (i >= formats.length || gen !== this._hypnoGen) {
                if (i >= formats.length) console.warn(`[Audio] hypno voice: no decodable format for ${name}`);
                return;
            }
            fetch(`assets/audio/hypno/${name}.${formats[i]}`)
                .then(r => { if (!r.ok) throw new Error('http ' + r.status); return r.arrayBuffer(); })
                .then(arr => ctx.decodeAudioData(arr))
                .then(buf => { this._hypnoBuffers.set(name, buf); startBuf(buf); })
                .catch(() => tryLoad(i + 1));   // next format (or give up silently)
        };
        try { tryLoad(0); } catch (e) { /* fetch/decode unsupported */ }
    }

    /** Stop the hypno voice. RAW Web Audio: gain.value=0 is the fundamental,
     *  FF152-reliable mute (it's a GainNode param, NOT a media-element property —
     *  the "can't stop" bug is specific to <audio>/<video> + createMediaElement-
     *  Source; a GainNode always silences). Then stop the BufferSource. Bumps the
     *  generation counter so an in-flight decode->start won't fire after a stop. */
    stopHypnoVoice() {
        this._hypnoGen = (this._hypnoGen || 0) + 1;   // cancel any pending decode->startBuf
        this._hypnoHowlName = '';
        try { if (this._hypnoGain) this._hypnoGain.gain.value = 0; } catch (e) {}
        try { if (this._hypnoSource) { try { this._hypnoSource.stop(0); } catch (e2) {} this._hypnoSource.disconnect(); } } catch (e) {}
        try { if (this._hypnoGain) this._hypnoGain.disconnect(); } catch (e) {}
        this._hypnoSource = null; this._hypnoGain = null;
        // Drop any legacy Howl from an earlier build/path (defensive, normally null).
        if (this._hypnoHowl) { try { this._hypnoHowl.volume(0); } catch (e) {} try { this._hypnoHowl.stop(); } catch (e) {} try { this._hypnoHowl.unload(); } catch (e) {} this._hypnoHowl = null; }
        this._duckBgmForHypno(false);   // restore game BGM after the hypno voice ends/stops
    }

    /** Duck the game BGM while a hypno voice plays (the clip has its own ambient
     *  music — layering the scene BGM on top is muddy, foundby:owner 2026-06-22).
     *  Debounced un-duck: a new clip starting cancels the pending restore, so the
     *  BGM never blips back up between consecutive hypno beats. Real leave (no new
     *  voice within 250ms) fades the BGM back over 500ms. */
    _duckBgmForHypno(on) {
        try {
            if (on) {
                if (this._bgmUnduckTimer) { clearTimeout(this._bgmUnduckTimer); this._bgmUnduckTimer = null; }
                if (this._bgm) { try { this._bgm.volume(0); } catch (e) {} }
                this._bgmDuckedForHypno = true;
            } else if (this._bgmDuckedForHypno) {
                if (this._bgmUnduckTimer) { clearTimeout(this._bgmUnduckTimer); }
                this._bgmUnduckTimer = setTimeout(() => {
                    this._bgmUnduckTimer = null;
                    this._bgmDuckedForHypno = false;
                    if (this._bgm && this._bgmVolume > 0) {
                        try { this._bgm.fade(this._bgm.volume(), this._bgmVolume, 500); }
                        catch (e) { try { this._bgm.volume(this._bgmVolume); } catch (e2) {} }
                    }
                }, 250);
            }
        } catch (e) { /* never let ducking break voice teardown */ }
    }

    /** 🔊 toggle — now governs the Howler voice volume (0 = muted). */
    setHypnoVoiceMuted(muted) {
        this._hypnoMuted = !!muted;
        if (this._hypnoHowl) {
            try { this._hypnoHowl.volume(this._hypnoMuted ? 0 : this._hypnoVolume); } catch (e) {}
        }
        // legacy gain route, if ever present — keep in sync (harmless no-op now)
        if (this._hypnoGain) {
            try { this._hypnoGain.gain.value = this._hypnoMuted ? 0 : this._hypnoVolume; } catch (e) {}
        }
    }

    get hypnoVoiceMuted() { return this._hypnoMuted; }
    isHypnoVoicePlaying() { return !!this._hypnoSource; }

    // Dedicated Hypno Sound slider (Settings) — only the hypno voice.
    get hypnoVolume() { return this._hypnoVolume; }
    set hypnoVolume(val) {
        this._hypnoVolume = Math.max(0, Math.min(1, val));
        try { localStorage.setItem('HypnoVolume', String(this._hypnoVolume)); } catch (e) { /* iframe storage blocked */ }
        // Live-apply to the playing hypno voice (Howl) so the slider drags in real time.
        if (this._hypnoHowl && !this._hypnoMuted) {
            try { this._hypnoHowl.volume(this._hypnoVolume); } catch (e) {}
        }
        if (this._hypnoGain && !this._hypnoMuted) {
            try { this._hypnoGain.gain.value = this._hypnoVolume; } catch (e) {}
        }
    }

    // ---- SFX ----

    playSFX(sfxName) {
        if (!sfxName) return;
        if (this._sfxFailed.has(sfxName)) return;

        const url = this._sfxUrl(sfxName);

        // Reuse cached Howl or create new one
        let howl = this._sfxCache.get(sfxName);
        if (howl) {
            // LRU: move to end of insertion order so FIFO eviction becomes LRU
            this._sfxCache.delete(sfxName);
            this._sfxCache.set(sfxName, howl);
        } else {
            howl = new Howl({
                src: url,
                volume: this._sfxVolume,
                onloaderror: () => {
                    console.warn(`[Audio] SFX not found: ${url}`);
                    this._sfxCache.delete(sfxName);
                    this._sfxFailed.add(sfxName);
                },
            });
            this._sfxCache.set(sfxName, howl);

            // Limit cache size (evicts least-recently-used due to LRU re-insertion above)
            if (this._sfxCache.size > 30) {
                const oldest = this._sfxCache.keys().next().value;
                this._sfxCache.get(oldest)?.unload();
                this._sfxCache.delete(oldest);
            }
        }

        howl.volume(this._sfxVolume);
        howl.play();
    }

    /** Convenience: UI click sound */
    playUIClick() { this.playSFX('mouseclick1'); }
    playUIOpen() { this.playSFX('pop1'); }
    playUIClose() { this.playSFX('click_back1'); }
    playPurchase() { this.playSFX('item_purchase1'); }

    // ---- Helpers ----

    _loadFloat(key, fallback) {
        // itch.io HTML5 iframes on Chrome with strict third-party storage
        // blocking throw DOMException on any localStorage access. AudioManager
        // is constructed at top-level module init, so an unguarded throw here
        // kills the entire ES6 module graph and the player sees ERR:Uncaught
        // under the loading bar with infinite load. Player report 2026-05-09.
        let val;
        try { val = localStorage.getItem(key); }
        catch(e) { return fallback; }
        if (val === null) return fallback;
        const num = parseFloat(val);
        return isNaN(num) ? fallback : num;
    }

    /**
     * Build URL array for a track — uses audio_manifest.json when loaded
     * to list ONLY extensions that exist on disk. Without the manifest,
     * Howler is handed [ogg, mp3, wav] and tries each in turn — but on
     * Android stock WebView (Infinix) the SPA's catch-all 404 returns
     * index.html, which HTML5 Audio interprets as malformed media (error=4)
     * and the array fallback fails for tracks where only one ext exists.
     *
     * `prefer` orders the result: first hit wins on browsers that pick
     * the first-supported source. Manifest-listed extensions are filtered
     * by `prefer` order; unlisted exts are dropped.
     */
    _audioUrl(category, name, prefer) {
        const base = `assets/audio/${category}/${name}`;
        const manifest = this._audioManifest && this._audioManifest[category];
        const have = manifest && manifest[name];
        if (have && have.length) {
            const set = new Set(have);
            return prefer.filter(e => set.has(e)).map(e => `${base}.${e}`);
        }
        // Legacy fallback (manifest not loaded yet, or track unknown).
        return prefer.map(e => `${base}.${e}`);
    }

    _bgmUrl(name)     { return this._audioUrl('bgm',     name, ['ogg', 'mp3', 'wav']); }
    _sfxUrl(name)     { return this._audioUrl('sfx',     name, ['ogg', 'wav', 'mp3']); }
    _ambientUrl(name) { return this._audioUrl('ambient', name, ['ogg', 'mp3', 'wav']); }
    // OGG/Opus first, m4a fallback. Firefox + Howler handle ogg far more reliably
    // than m4a/AAC for play/stop (Howler #474 — m4a caused the FF "won't stop /
    // intermittent" class). Howler picks the first playable source from the array.
    _hypnoUrl(name)   { return [`assets/audio/hypno/${name}.ogg`, ...this._audioUrl('hypno', name, ['m4a'])]; }

    /**
     * Get current state for debugging.
     */
    getState() {
        return {
            bgmTrack: this._bgmTrack,
            ambientTrack: this._ambientTrack,
            bgmVolume: this._bgmVolume,
            sfxVolume: this._sfxVolume,
        };
    }
}

export const audioManager = new AudioManager();
